import {
  drugDataService,
  type DrugDataSearchRequest,
} from '@/entities/drug-data/api/drug-data.api';
import { commissionKeys } from '@/entities/commission/hooks/useCommissions';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const drugDataKeys = {
  all: ['drug-data'] as const,
  flags: (baseYm: string) => [...drugDataKeys.all, 'flags', baseYm] as const,
  release: (baseYm: string) =>
    [...drugDataKeys.all, 'release', baseYm] as const,
  drugs: (search: DrugDataSearchRequest, pageable: Pageable) =>
    [...drugDataKeys.all, 'drugs', search, pageable] as const,
};

export const useDrugDataFlags = (baseYm: string) =>
  useQuery({
    queryKey: drugDataKeys.flags(baseYm),
    queryFn: () => drugDataService.getFlags(baseYm),
    enabled: Boolean(baseYm),
  });

export const useDrugDataReleaseStatus = (baseYm: string) =>
  useQuery({
    queryKey: drugDataKeys.release(baseYm),
    queryFn: () => drugDataService.getReleaseStatus(baseYm),
    enabled: Boolean(baseYm),
  });

export const useDrugDataDrugList = (
  search: DrugDataSearchRequest,
  pageable: Pageable,
) =>
  usePaginatedQuery({
    queryKeyPrefix: drugDataKeys.all,
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      drugDataService.searchDrugs(s, p),
    enabled: Boolean(search.refYm),
  });

export const useSetDrugDataFlags = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: drugDataService.setFlags,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: drugDataKeys.flags(data.baseYm),
      });
    },
  });
};

export const useReleaseDrugData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (baseYm: string) => drugDataService.releaseDrugData(baseYm),
    onSuccess: (_, baseYm) => {
      queryClient.invalidateQueries({ queryKey: drugDataKeys.release(baseYm) });
      queryClient.invalidateQueries({ queryKey: drugDataKeys.flags(baseYm) });
    },
  });
};

/** 게시(개시) 취소 — 앱은 직전에 게시된 기준월 데이터로 돌아간다 */
export const useCancelDrugDataRelease = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (baseYm: string) =>
      drugDataService.cancelDrugDataRelease(baseYm),
    onSettled: (_, __, baseYm) => {
      queryClient.invalidateQueries({ queryKey: drugDataKeys.release(baseYm) });
      queryClient.invalidateQueries({ queryKey: drugDataKeys.flags(baseYm) });
    },
  });
};

/**
 * 생성 단계 초기화.
 * 응답 flags 로 단계 카드를 바로 갱신하고, 지워진 의약품·수수료 목록도 다시 불러온다.
 * 실패해도 서버가 플래그를 먼저 내려 두므로 onSettled 에서 다시 조회한다.
 */
export const useResetDrugData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: drugDataService.resetDrugData,
    onSuccess: (data, { baseYm }) => {
      queryClient.setQueryData(drugDataKeys.flags(baseYm), data.flags);
      queryClient.invalidateQueries({ queryKey: commissionKeys.all });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: drugDataKeys.all });
    },
  });
};

export const useValidateCommissionDrugCodes = () =>
  useMutation({
    mutationFn: (baseYm: string) =>
      drugDataService.validateCommissionDrugCodes(baseYm),
  });

export const useRunDataCollectionJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: drugDataService.runDataCollectionJob,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: drugDataKeys.flags(variables.baseYm),
      });
    },
  });
};

export const useMergeHiraDrug = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (baseYm: string) => drugDataService.mergeHiraDrug(baseYm),
    onSuccess: (_, baseYm) => {
      queryClient.invalidateQueries({ queryKey: drugDataKeys.flags(baseYm) });
    },
  });
};

export const useUploadCommissionExcel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => drugDataService.uploadCommission(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: drugDataKeys.all });
      queryClient.invalidateQueries({ queryKey: commissionKeys.all });
    },
  });
};

export const useUpdateDrugInfo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (baseYm: string) => drugDataService.updateDrugInfo(baseYm),
    onSuccess: (_, baseYm) => {
      queryClient.invalidateQueries({ queryKey: drugDataKeys.flags(baseYm) });
    },
  });
};

export const useProcessHiraNonBenefit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      baseYm,
      file,
      remotePath,
      downloadFileName,
    }: {
      baseYm: string;
      file?: File;
      remotePath?: string;
      downloadFileName?: string;
    }) =>
      drugDataService.processHiraNonBenefit(baseYm, file, {
        remotePath,
        downloadFileName,
      }),
    onSuccess: (_, { baseYm }) => {
      queryClient.invalidateQueries({ queryKey: drugDataKeys.flags(baseYm) });
    },
  });
};

export const useCreateCodeMapping = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ baseYm, file }: { baseYm: string; file?: File }) =>
      drugDataService.createCodeMapping(baseYm, file),
    onSuccess: (_, { baseYm }) => {
      queryClient.invalidateQueries({ queryKey: drugDataKeys.flags(baseYm) });
    },
  });
};

export const useInsertDrugsManual = () =>
  useMutation({
    mutationFn: (items: Record<string, unknown>[]) =>
      drugDataService.insertDrugsManual(items),
  });

export const useUpdateDrugDataDrug = () =>
  useMutation({
    mutationFn: ({
      refYm,
      drugCode,
      body,
    }: {
      refYm: string;
      drugCode: string;
      body: Record<string, unknown>;
    }) => drugDataService.updateDrugDataDrug(refYm, drugCode, body),
  });
